import { useNavigate } from "react-router-dom"

import { Input } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { useFeatureFlags, useUser } from "@/hooks/useUser"
import { FilterOutlined } from "@/icons"
import { InformationAlert } from "@/molecules/InformationAlert"
import { DetailsHandler, Form, RESOURCES, Rules } from "@/organisms/Form"
import { ResourcesList } from "@/organisms/Form/components/ResourcesList"
import {
  useFiltersScopes,
  useScope
} from "@/pages/SettingsPage/application/scopes/useScopes"
import { withoutResource } from "@/pages/SettingsPage/application/scopes/useScopes.selector"
import { getInitialValues } from "./helpers"

import styles from "./ScopeForm.module.scss"

export const ScopeForm = ({ creator, updater }: any) => {
  const { t } = useTranslation()
  const { t: tForm } = useTranslation("translation", {
    keyPrefix: "settings.scopes.form"
  })

  const { response } = useFiltersScopes()
  const { data: allOptions = RESOURCES.DEFAULT_OPTIONS } = response || {}
  const vmInstancesEnabled = useFeatureFlags("vmInstancesEnabled")
  const options = vmInstancesEnabled
    ? allOptions
    : withoutResource(allOptions, "Virtual Machine")
  const { data, isLoading } = useScope()
  const isAllResources = !!data?.isAllResources
  const { form, formId, isNewEntity, permissions, message } = Form.useForm()
  const navigate = useNavigate()
  const initialValues = getInitialValues(data)

  const disabled = !permissions.write || isAllResources
  const { user } = useUser()

  return (
    <Form
      key={initialValues.id}
      id={formId}
      loading={isLoading}
      initialValues={initialValues}
      onFinish={async (values) => {
        if (isAllResources) return

        const details = new DetailsHandler()
        const { name, description } = details.handle(values)

        if (typeof name !== "string") return // for typecasting, should be unreachable state due to validation

        const scope = {
          name,
          description,
          conditions: values.specific_resources
        }
        const action = isNewEntity ? "created" : "updated"

        try {
          const data = { scope }

          if (isNewEntity) {
            const response = await creator.mutateAsync(data)
            const id = response.data[0].id

            navigate(`/settings/scopes/${id}`, { replace: true })
          } else {
            const { currentScopeId } = user || {}
            const isActiveScope = values.id === currentScopeId
            const payload = { isActiveScope, data, needle: `/${values.id}` }

            await updater.mutateAsync(payload)
          }

          message.onSuccess(name, { action })
        } catch (error) {
          message.onFailed(name, { action, error })
        }
      }}
    >
      {isAllResources && (
        <InformationAlert
          id={`allResourcesInfoMessage-${formId}`}
          message={tForm("allResourcesInfoMessage")}
          className={styles.informationAlert}
        />
      )}
      <Form.Item hidden name="preview" />
      <Form.Section.Details
        disabled={disabled}
        label={tForm("label.name")}
        suffix={
          !isNewEntity && (
            <Form.Item name="id" label="ID">
              <Input disabled />
            </Form.Item>
          )
        }
        nameRules={[Rules.getNoCommasRule(t)]}
      />
      <Form.Section.Template
        title={t("forms.sections.resources.title")}
        Icon={FilterOutlined}
      >
        <ResourcesList
          disabled={disabled}
          form={form}
          options={options}
          isAllResources={isAllResources}
        />
      </Form.Section.Template>
    </Form>
  )
}
